import { Component, EventEmitter, Input, Output, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Dialog } from 'primeng/dialog';
import { SharedModule } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { toast } from 'ngx-sonner';
import * as XLSX from 'xlsx';

import { AppButton } from '../../../../../../shared/components/app-button/app-button';
import {
  SettingsService,
  ImportDiagnosticRow,
  ImportDiagnosticsResult,
} from '../../../../../../core/services/settings.service';

interface PreviewRow extends ImportDiagnosticRow {
  isValid: boolean;
  error?: string;
}

/** The five columns of the MetaSmile catalogue sheet, and what they map to. */
type FieldKey = keyof ImportDiagnosticRow;

@Component({
  selector: 'app-dtp-import-diagnostics-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule, AppButton, Dialog, SharedModule],
  template: `
    <p-dialog
      [visible]="true"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      styleClass="!rounded-[2rem] overflow-hidden shadow-2xl w-full max-w-[95vw]"
      (onHide)="onClose()"
      [style]="{ width: '1100px', 'max-height': '95vh' }"
    >
      <ng-template pTemplate="header">
        <div class="flex items-center gap-4 w-full pr-8">
          <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 flex items-center justify-center border border-primary/10">
            <i class="pi pi-bookmark text-lg text-primary"></i>
          </div>
          <div>
            <h3 class="text-lg font-bold text-slate-800 tracking-tight">
              {{ 'DIAG_IMPORT.TITLE' | translate }}
            </h3>
            <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              {{ 'DIAG_IMPORT.SUBTITLE' | translate }}
            </p>
          </div>
        </div>
      </ng-template>

      <div class="flex flex-col w-full py-2">
        <!-- Step 3: what the import actually did -->
        <div *ngIf="result" class="flex flex-col items-center justify-center py-16 px-6 text-center">
          <div class="w-20 h-20 rounded-[2rem] bg-emerald-50 border border-emerald-100 flex items-center justify-center mb-6">
            <i class="pi pi-check-circle text-emerald-500" style="font-size: 2.5rem"></i>
          </div>
          <h4 class="text-2xl font-bold text-slate-800 mb-2">{{ 'DIAG_IMPORT.DONE_TITLE' | translate }}</h4>

          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full max-w-2xl mt-6">
            <div class="flex flex-col items-center gap-1 px-4 py-4 rounded-2xl bg-emerald-50 border border-emerald-100">
              <span class="text-2xl font-black text-emerald-600">{{ result.inserted }}</span>
              <span class="text-[9px] font-black uppercase tracking-widest text-emerald-500">{{ 'DIAG_IMPORT.ADDED' | translate }}</span>
            </div>
            <div class="flex flex-col items-center gap-1 px-4 py-4 rounded-2xl bg-sky-50 border border-sky-100">
              <span class="text-2xl font-black text-sky-600">{{ result.updated }}</span>
              <span class="text-[9px] font-black uppercase tracking-widest text-sky-500">{{ 'DIAG_IMPORT.UPDATED' | translate }}</span>
            </div>
            <div class="flex flex-col items-center gap-1 px-4 py-4 rounded-2xl bg-slate-50 border border-slate-100">
              <span class="text-2xl font-black text-slate-500">{{ result.skipped_rows }}</span>
              <span class="text-[9px] font-black uppercase tracking-widest text-slate-400">{{ 'DIAG_IMPORT.SKIPPED' | translate }}</span>
            </div>
            <div class="flex flex-col items-center gap-1 px-4 py-4 rounded-2xl bg-slate-50 border border-slate-100">
              <span class="text-2xl font-black text-slate-500">{{ result.rows_in_file }}</span>
              <span class="text-[9px] font-black uppercase tracking-widest text-slate-400">{{ 'DIAG_IMPORT.IN_FILE' | translate }}</span>
            </div>
          </div>

          <p class="text-xs font-medium text-slate-400 max-w-lg mt-6 leading-relaxed">
            {{ 'DIAG_IMPORT.DONE_HINT' | translate }}
          </p>
        </div>

        <!-- Step 1: pick a file -->
        <div *ngIf="!result && !previewData.length" class="flex flex-col items-center justify-center py-16">
          <div class="w-full max-w-2xl p-14 border-2 border-dashed rounded-[2.5rem] transition-all duration-300 cursor-pointer relative flex flex-col items-center text-center"
               [ngClass]="isDragging ? 'border-primary bg-primary/5 scale-[1.01] shadow-xl shadow-primary/10' : 'border-slate-200 bg-slate-50 hover:border-primary/40 hover:bg-white hover:shadow-sm'"
               (click)="fileInput.click()"
               (dragover)="onDragOver($event)"
               (dragleave)="onDragLeave($event)"
               (drop)="onDrop($event)">
            <input type="file" #fileInput (change)="onFileSelected($event)" accept=".xlsx,.xls,.csv" class="hidden">

            <div class="w-24 h-24 rounded-[2.5rem] bg-white shadow-sm border border-slate-100 flex items-center justify-center mb-6 transition-all duration-500"
                 [class.scale-110]="isDragging"
                 [class.-translate-y-2]="isDragging">
              <i class="pi pi-file-excel text-emerald-500" style="font-size: 3rem"></i>
            </div>
            <h4 class="text-2xl font-bold text-slate-800 mb-2">
              {{ isDragging ? ('DIAG_IMPORT.DROP_TO_UPLOAD' | translate) : ('DIAG_IMPORT.UPLOAD_FILE' | translate) }}
            </h4>
            <p class="text-sm font-medium text-slate-400 max-w-md mb-6 leading-relaxed">
              {{ 'DIAG_IMPORT.UPLOAD_HINT' | translate }}
            </p>

            <!-- The sheet's own column names, so nobody has to guess the shape. -->
            <div class="flex flex-wrap items-center justify-center gap-2 mb-8">
              <span *ngFor="let c of expectedColumns"
                class="px-3 py-1 rounded-full bg-white border border-slate-200 text-[10px] font-bold text-slate-500">
                {{ c | translate }}
              </span>
            </div>

            <app-button
              [label]="isDragging ? ('DIAG_IMPORT.RELEASE_FILE' | translate) : ('DIAG_IMPORT.BROWSE_FILES' | translate)"
              icon="pi pi-search"
              [variant]="isDragging ? 'primary' : 'outline'"></app-button>
          </div>
        </div>

        <!-- Step 2: preview -->
        <div *ngIf="!result && previewData.length" class="space-y-4 px-2">
          <div class="flex flex-wrap items-center justify-between gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
            <div class="flex flex-wrap items-center gap-3">
              <div class="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 text-white text-[11px] font-bold uppercase tracking-wider shadow-sm shadow-emerald-200">
                <i class="pi pi-check-circle"></i> {{ validCount }} {{ 'DIAG_IMPORT.VALID' | translate }}
              </div>
              <div *ngIf="invalidCount > 0"
                class="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500 text-white text-[11px] font-bold uppercase tracking-wider shadow-sm shadow-rose-200">
                <i class="pi pi-exclamation-circle"></i> {{ invalidCount }} {{ 'DIAG_IMPORT.INVALID' | translate }}
              </div>
              <div class="h-6 w-[1px] bg-slate-200 mx-1"></div>
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {{ fileName }}
              </p>
            </div>
            <button (click)="clearPreview()"
              class="flex items-center gap-2 text-[11px] font-bold text-slate-400 hover:text-rose-500 transition-colors uppercase tracking-widest px-4 py-2 rounded-xl hover:bg-rose-50 border border-transparent hover:border-rose-100">
              <i class="pi pi-refresh"></i> {{ 'DIAG_IMPORT.RESET' | translate }}
            </button>
          </div>

          <!-- A file with no ID column can only ever add. Say so before importing,
               not after, because that is the difference between a re-import that
               updates and one that duplicates. -->
          <div *ngIf="!hasCodeColumn"
            class="flex items-start gap-3 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-100">
            <i class="pi pi-exclamation-triangle text-amber-500 mt-0.5"></i>
            <p class="text-[11px] font-medium text-amber-700 leading-relaxed">
              {{ 'DIAG_IMPORT.NO_CODE_WARNING' | translate }}
            </p>
          </div>

          <div class="border border-slate-100 rounded-2xl overflow-hidden shadow-sm bg-white">
            <div class="overflow-x-auto overflow-y-auto max-h-[50vh] custom-scrollbar">
              <table class="w-full text-left border-collapse">
                <thead class="sticky top-0 z-20">
                  <tr class="bg-slate-800 text-white">
                    <th class="px-4 py-3 text-[9px] font-black uppercase tracking-widest border-r border-slate-700 w-10"></th>
                    <th class="px-4 py-3 text-[9px] font-black uppercase tracking-widest border-r border-slate-700">{{ 'DIAG_IMPORT.COL_NAME' | translate }}</th>
                    <th class="px-4 py-3 text-[9px] font-black uppercase tracking-widest border-r border-slate-700">{{ 'DIAG_IMPORT.COL_FAMILY' | translate }}</th>
                    <th class="px-4 py-3 text-[9px] font-black uppercase tracking-widest border-r border-slate-700">{{ 'DIAG_IMPORT.COL_SUBFAMILY' | translate }}</th>
                    <th class="px-4 py-3 text-[9px] font-black uppercase tracking-widest border-r border-slate-700">{{ 'DIAG_IMPORT.COL_TYPE' | translate }}</th>
                    <th class="px-4 py-3 text-[9px] font-black uppercase tracking-widest">{{ 'DIAG_IMPORT.COL_CODE' | translate }}</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                  <tr *ngFor="let row of previewData" class="transition-colors"
                      [ngClass]="row.isValid ? 'hover:bg-slate-50' : 'bg-rose-50/40 hover:bg-rose-50'">
                    <td class="px-4 py-2.5 border-r border-slate-50">
                      <div class="w-5 h-5 rounded-md flex items-center justify-center"
                           [ngClass]="row.isValid ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'"
                           [title]="row.error || ''">
                        <i class="pi text-[8px]" [ngClass]="row.isValid ? 'pi-check' : 'pi-times'"></i>
                      </div>
                    </td>
                    <td class="px-4 py-2.5 text-[11px] font-semibold border-r border-slate-50"
                        [ngClass]="row.isValid ? 'text-slate-700' : 'text-rose-400'">
                      {{ row.name || ('DIAG_IMPORT.MISSING_NAME' | translate) }}
                    </td>
                    <td class="px-4 py-2.5 text-[11px] text-slate-500 border-r border-slate-50">{{ row.family || '—' }}</td>
                    <td class="px-4 py-2.5 text-[11px] text-slate-500 border-r border-slate-50">{{ row.subfamily || '—' }}</td>
                    <td class="px-4 py-2.5 text-[11px] text-slate-500 border-r border-slate-50">{{ row.diagnostic_type || '—' }}</td>
                    <td class="px-4 py-2.5 text-[11px] font-mono text-slate-500">{{ row.external_code || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex items-center justify-between w-full pt-4 px-2">
          <div class="text-slate-400 text-[10px] font-bold flex items-center gap-2 uppercase tracking-widest"
               *ngIf="!result && previewData.length && hasCodeColumn">
            <i class="pi pi-shield-check text-emerald-500"></i>
            {{ 'DIAG_IMPORT.FOOTER_HINT' | translate }}
          </div>
          <div *ngIf="result || !previewData.length || !hasCodeColumn"></div>
          <div class="flex gap-3">
            <app-button
              [label]="(result ? 'DIAG_IMPORT.DONE' : 'DIAG_IMPORT.CANCEL') | translate"
              type="secondary"
              variant="ghost"
              (onClick)="onClose()"></app-button>
            <app-button *ngIf="!result && previewData.length"
                        [label]="'DIAG_IMPORT.IMPORT' | translate"
                        icon="pi pi-cloud-upload"
                        [disabled]="validCount === 0 || isSaving"
                        [loading]="isSaving"
                        (onClick)="commitImport()"></app-button>
          </div>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    :host { display: block; }
    .custom-scrollbar::-webkit-scrollbar { height: 8px; width: 8px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: #f1f5f9; border-radius: 10px; }
    .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
  `]
})
export class DtpImportDiagnosticsModal {
  /** The branch the catalogue is imported into. Required. */
  @Input() branchId: number | null = null;

  @Output() close = new EventEmitter<void>();
  /** Fired after a successful import so the list behind can refresh. */
  @Output() saved = new EventEmitter<void>();

  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);

  previewData: PreviewRow[] = [];
  fileName = '';
  isDragging = false;
  isSaving = false;
  validCount = 0;
  invalidCount = 0;
  hasCodeColumn = false;
  result: ImportDiagnosticsResult | null = null;

  /** Shown as hint chips. Translated, because the sheet itself may be in
   *  Portuguese or English and the parser accepts either. */
  readonly expectedColumns = [
    'DIAG_IMPORT.COL_FAMILY',
    'DIAG_IMPORT.COL_SUBFAMILY',
    'DIAG_IMPORT.COL_NAME',
    'DIAG_IMPORT.COL_TYPE',
    'DIAG_IMPORT.COL_CODE',
  ];

  onClose() {
    this.close.emit();
  }

  // ── Drag & drop ──────────────────────────────────────────────────────
  onDragOver(e: DragEvent) {
    e.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(e: DragEvent) {
    e.preventDefault();
    this.isDragging = false;
  }

  onDrop(e: DragEvent) {
    e.preventDefault();
    this.isDragging = false;
    const file = e.dataTransfer?.files?.[0];
    if (file) this.readFile(file);
  }

  onFileSelected(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.readFile(file);
    // Let the same file be picked again after a reset.
    input.value = '';
  }

  clearPreview() {
    this.previewData = [];
    this.fileName = '';
    this.validCount = 0;
    this.invalidCount = 0;
    this.hasCodeColumn = false;
    this.cdr.detectChanges();
  }

  // ── Parsing ──────────────────────────────────────────────────────────

  private readFile(file: File) {
    this.fileName = file.name;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(new Uint8Array(ev.target?.result as ArrayBuffer), { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        if (!sheet) throw new Error('empty workbook');
        this.buildPreview(sheet);
      } catch {
        toast.error(this.translate.instant('DIAG_IMPORT.PARSE_FAILED'));
      }
      this.cdr.detectChanges();
    };
    reader.readAsArrayBuffer(file);
  }

  /** Strip accents and case so "Subfamília" and "SUBFAMILIA" match alike. */
  private norm(v: unknown): string {
    return String(v ?? '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();
  }

  /**
   * Which field, if any, a header cell names.
   *
   * The catalogue comes in Portuguese OR English, so every test accepts both:
   * "Diagnóstico / condição" and "Diagnosis / condition", "ID MetaSmile" and
   * "MetaSmile ID". Note the stems are deliberately short - "diagnos" rather
   * than "diagnost", because the English "diagnosis" has no T.
   *
   * Two orderings matter:
   *  - subfamily before family, since "subfamilia"/"subfamily" contains the
   *    looser "famil" and would otherwise be swallowed by it;
   *  - the code column before the name, so a header like "Diagnosis code"
   *    resolves to the code rather than to the diagnosis.
   */
  private fieldFor(header: unknown): FieldKey | null {
    const h = this.norm(header);
    if (!h) return null;
    if (h.includes('subfamil')) return 'subfamily';
    if (h.includes('famil')) return 'family';
    if (h.includes('metasmile') || h === 'id' || h.includes('codigo') || h.includes('code')) {
      return 'external_code';
    }
    if (h.includes('diagnos') || h.includes('condic') || h.includes('condit')) return 'name';
    if (h.includes('tipo') || h.includes('type')) return 'diagnostic_type';
    return null;
  }

  /**
   * The real header is not always row 1.
   *
   * The MetaSmile sheet opens with a merged title banner, so reading it with
   * sheet_to_json's defaults names every column `__EMPTY` and the whole import
   * silently maps to nothing. Scan the first handful of rows and take the first
   * one that actually names two or more of the columns we know.
   */
  private buildPreview(sheet: XLSX.WorkSheet) {
    const grid = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, blankrows: false, defval: '' });

    let headerIdx = -1;
    let mapping: Record<number, FieldKey> = {};
    for (let i = 0; i < Math.min(grid.length, 15); i++) {
      const candidate: Record<number, FieldKey> = {};
      (grid[i] || []).forEach((cell, col) => {
        const field = this.fieldFor(cell);
        // First column wins a field, so a stray second "Tipo" cannot steal it.
        if (field && !Object.values(candidate).includes(field)) candidate[col] = field;
      });
      const fields = Object.values(candidate);
      if (fields.includes('name') && fields.length >= 2) {
        headerIdx = i;
        mapping = candidate;
        break;
      }
    }

    if (headerIdx === -1) {
      toast.error(this.translate.instant('DIAG_IMPORT.NO_HEADER'));
      return;
    }

    this.hasCodeColumn = Object.values(mapping).includes('external_code');

    const rows: PreviewRow[] = [];
    for (let i = headerIdx + 1; i < grid.length; i++) {
      const raw = grid[i] || [];
      const row: PreviewRow = { name: '', isValid: true };
      for (const [col, field] of Object.entries(mapping)) {
        const value = String(raw[Number(col)] ?? '').trim();
        if (field === 'name') row.name = value;
        else row[field] = value || null;
      }
      // An entirely blank line is spacing, not a rejected row - skip it quietly.
      const anyValue =
        row.name || row.family || row.subfamily || row.diagnostic_type || row.external_code;
      if (!anyValue) continue;

      if (!row.name) {
        row.isValid = false;
        row.error = this.translate.instant('DIAG_IMPORT.MISSING_NAME');
      }
      rows.push(row);
    }

    this.previewData = rows;
    this.validCount = rows.filter((r) => r.isValid).length;
    this.invalidCount = rows.length - this.validCount;

    if (!rows.length) toast.error(this.translate.instant('DIAG_IMPORT.NO_ROWS'));
  }

  // ── Import ───────────────────────────────────────────────────────────

  async commitImport() {
    if (!this.branchId) {
      toast.error(this.translate.instant('DIAG_IMPORT.NO_BRANCH'));
      return;
    }
    const payload: ImportDiagnosticRow[] = this.previewData
      .filter((r) => r.isValid)
      .map(({ name, family, subfamily, diagnostic_type, external_code }) => ({
        name,
        family,
        subfamily,
        diagnostic_type,
        external_code,
      }));

    if (!payload.length) return;

    this.isSaving = true;
    this.cdr.detectChanges();
    try {
      this.result = await firstValueFrom(
        this.settingsService.importDiagnostics(this.branchId, payload)
      );
      toast.success(this.translate.instant('DIAG_IMPORT.SUCCESS'));
      // Refresh the list behind the modal; the modal stays open on the summary
      // so the counts can actually be read.
      this.saved.emit();
    } catch (err: any) {
      // import_diagnostics_v1 is SECURITY INVOKER, so diagnostics_tb's own RLS
      // decides this - and that policy matches the caller's users_tb branch, not
      // the branch passed in. Unreachable from here (the screen's branch IS the
      // user's own), but a raw "new row violates row-level security policy" is
      // not something to show a receptionist.
      const denied = err?.code === '42501' || /row-level security/i.test(err?.message || '');
      toast.error(
        denied
          ? this.translate.instant('DIAG_IMPORT.NOT_ALLOWED')
          : err?.message || this.translate.instant('DIAG_IMPORT.FAILED')
      );
    } finally {
      this.isSaving = false;
      this.cdr.detectChanges();
    }
  }
}
